import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
  where,
} from 'firebase/firestore';
import { useUser } from 'reactfire';
import CircularProgress from '@mui/material/CircularProgress';
import useGlobal from 'global-state/store';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import OurPagination from 'components/OurPagination';
import moment from 'moment';
import TankDetail from './TankDetail';
import TankListHeaders from './TankListHeaders';
import TankListItem from './TankListItem';
import TankFilters from './TankFilters';
import ClientsSelect from '../ClientsSelect';

export default function Tanks() {
  const { t } = useTranslation();
  const analytics = getAnalytics();
  const { data: user } = useUser();
  const [globalState] = useGlobal();
  const [clientOrg, setClientOrg] = useState(Object.keys(globalState.clientOrganizations)[0]);

  const computeListOfConditions = useCallback((formikValues) => {
    let conditions = [
      where('lastModified', '<=', formikValues.endDate.toDate()),
      where('lastModified', '>=', formikValues.startDate.toDate()),
    ];
    if (formikValues.transporterName !== '') {
      conditions = [...conditions, where('transporterName', '==', formikValues.transporterName)];
    }
    return conditions;
  }, []);

  const formikInitValues = useMemo(() => ({
    transporterName: '',
    startDate: initialStartDate(),
    endDate: initialEndDate(),
  }), []);

  const [listOfConditions, setListOfConditions] = useState(computeListOfConditions(formikInitValues));

  function initialStartDate() {
    return moment().subtract(2, 'years');
  }

  function initialEndDate() {
    return moment().endOf('day');
  }

  useEffect(() => {
    if (globalState.activeOrganization !== '' && user?.uid) {
      logEvent(analytics, 'loading_truck_tanks', {
        user_uid: user?.uid,
        appName: 'Digitank, Gauger',
        organization: globalState.activeOrganization,
      });
    }
  }, [analytics, globalState.activeOrganization, user?.uid]);

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      }}
    >
      <Typography variant="h5" component="div" sx={{ mb: 2, mt: 2 }}>
        {t('admin.client_truck_tanks')}
      </Typography>
      <ClientsSelect updateCallback={(newOrg) => setClientOrg(newOrg)} />

      {clientOrg && (
        <OurPagination
          collectionPath={`organizations/${clientOrg}/apps/digitank-transporter/tanks`}
          listOfConditions={listOfConditions}
          orderByAttribute="lastModified"
          filters={(
            <TankListFilters
              formikInitValues={formikInitValues}
              setListOfConditions={setListOfConditions}
              computeListOfConditions={computeListOfConditions}
              clientOrg={clientOrg}
            />
          )}
          list={(
            <TankList listOfConditions={listOfConditions} />
          )}
        />
      )}
      {clientOrg === undefined && <CircularProgress />}
    </Box>
  );
}

function TankList({ docs, initSearch, listOfConditions }) {
  const [activeTank, setActiveTank] = useState('');
  const [open, setOpen] = useState(false);
  const [tanks, setTanks] = useState([]);
  const [tanksPath, setTanksPath] = useState({});

  const refresh = useCallback(async () => {
    const allTanks = [];
    const allTanksPaths = {};

    for (const tankDoc of docs) {
      const data = tankDoc.data();
      allTanks.push({ transporter: data.transporterName, ...data });
      allTanksPaths[tankDoc.data().id + data.transporterName] = tankDoc.ref.path;
    }
    setTanks(allTanks);
    setTanksPath(allTanksPaths);
  }, [docs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpen = (tank) => {
    setActiveTank(tank);
    setOpen(true);
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', width: '100%', gap: 1,
    }}
    >
      <TankListHeaders />
      <Paper elevation={0} sx={{ p: 1 }}>
        <List dense sx={{ width: '100%' }}>
          {tanks.map((tank, i) => (
            <div key={tank.id}>
              <TankListItem index={i} tank={tank} handleOpen={handleOpen} />
              <Divider />
            </div>
          ))}
        </List>
      </Paper>
      <TankDetail
        open={open}
        setOpen={setOpen}
        tank={activeTank}
        tankPath={tanksPath[activeTank.id + activeTank.transporter]}
        tankDeleteCallback={() => initSearch(listOfConditions)}
      />
    </Box>
  );
}

function TankListFilters({
  initSearch, formikInitValues, setListOfConditions, computeListOfConditions, clientOrg,
}) {
  const { t } = useTranslation();
  const validationSchema = Yup.object().shape({
    transporterName: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: formikInitValues,
    validationSchema,
    onSubmit: (values) => {
      const newConditions = computeListOfConditions(values);
      setListOfConditions(newConditions);
      initSearch(newConditions);
    },
  });

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', width: '100%',
    }}
    >
      <TankFilters clientOrg={clientOrg} formik={formik} datePickersLabel={t('admin.last_modified')} />
    </Box>
  );
}
